import { api } from '../api/client';

import { API_ITEMS_URL } from '../utils';

/**
 * The single-entry doors of a repeating group (keepr-api docs/SCHEMA.md § One
 * entry at a time, KPR-677). Each changes ONE entry and never carries the
 * others, so two people editing different entries of one item both land. Every
 * door answers `{ op, group, entryId, position, from?, item }`.
 *
 * `config` is the request config the item form's attestation retry passes
 * (`{ headers: { 'X-Attestation': … } }`): a card that wants its records
 * signed asks for a signature on these writes too.
 */

const base = (itemId, group) => `${API_ITEMS_URL}/${encodeURIComponent(itemId)}/entries/${encodeURIComponent(group)}`;

/** Add an entry (`{ elements }` or `{ section }`), at `position` (1-based) or the end. */
export const addEntry = (itemId, group, body, config) => api.post(base(itemId, group), body, config);

/** Change an entry: the columns named merge over its own, or a heading's `section`. */
export const updateEntry = (itemId, group, entryId, body, config) => (
	api.patch(`${base(itemId, group)}/${encodeURIComponent(entryId)}`, body, config)
);

/** Remove an entry. */
export const removeEntry = (itemId, group, entryId, config) => (
	api.delete(`${base(itemId, group)}/${encodeURIComponent(entryId)}`, config)
);

/** Move an entry to `position` (1-based, headings counted). */
export const moveEntry = (itemId, group, entryId, position, config) => (
	api.post(`${base(itemId, group)}/${encodeURIComponent(entryId)}/move`, { position }, config)
);

/**
 * Send one planned operation (utils/repeatingGroupEntries planEntryChanges).
 * Resolves the door's answer (`data`).
 */
export async function sendEntryOperation(itemId, group, op, config) {
	let response;
	if (op.op === 'add') {
		const body = op.section !== undefined ? { section: op.section } : { elements: op.elements };
		if (op.position) body.position = op.position;
		response = await addEntry(itemId, group, body, config);
	} else if (op.op === 'update') {
		response = await updateEntry(itemId, group, op.entryId, op.section !== undefined ? { section: op.section } : { elements: op.elements }, config);
	} else if (op.op === 'remove') {
		response = await removeEntry(itemId, group, op.entryId, config);
	} else {
		response = await moveEntry(itemId, group, op.entryId, op.position, config);
	}
	return response.data;
}
